//
// (c) 2016-2020 Mental Canvas Inc.
//
import {
    Camera,
    Frustum,
    Matrix4,
    PerspectiveCamera,
    Quaternion,
    Sphere,
    Spherical,
    Vector2,
    Vector3,
    Vector4
} from 'three';

export var parseBoolean = function(value:string): boolean {
    switch (value.toLowerCase()) {
        case 'true': case '1': case 'on': return true;
        case 'false': case '0': case 'off': return false;
        default:
            return undefined;
    }
}
///////////////////////////////////////////////////////////////////////////////
// _Math
//
export var _Math = {
    DEG2RAD: Math.PI / 180,
    RAD2DEG: 180 / Math.PI,
    clamp: function (value: number, min: number, max: number): number {
        return Math.max(min, Math.min(max, value));
    },
    sqr: function (x: number): number {
        return x * x;
    },
    lerpQuats: function ( qa: Quaternion, qb: Quaternion, qr: Quaternion, t: number ): Quaternion {
        
        if ( t === 0 ) return qr.copy( qa );
        if ( t === 1 ) return qr.copy( qb );

        if (qa.dot( qb ) < 0.0) {
            // a + ( ((-b)-a)*t )
            qr.w = qa.w + (-qb.w-qa.w)*t;
            qr.x = qa.x + (-qb.x-qa.x)*t;
            qr.y = qa.y + (-qb.y-qa.y)*t;
            qr.z = qa.z + (-qb.z-qa.z)*t;
        } else {
            // a + ( (b-a)*t )
            qr.w = qa.w + (qb.w-qa.w)*t;
            qr.x = qa.x + (qb.x-qa.x)*t;
            qr.y = qa.y + (qb.y-qa.y)*t;
            qr.z = qa.z + (qb.z-qa.z)*t;				
        }

        qr.normalize();

        return qr;
    },

    lerp: function (x: number, y: number, t: number): number {
        return (1 - t) * x + t * y;
    },

    equalVal: function (a: number, b: number, tolerance: number): boolean {
        return (Math.abs(a - b) < tolerance);
    },

    equalVec3: function ( va: Vector3, vb: Vector3, tolerance: number ): boolean {
        return _Math.equalVal( va.x, vb.x, tolerance ) && _Math.equalVal( va.y, vb.y, tolerance ) && _Math.equalVal( va.z, vb.z, tolerance );
    },

    equalQuat: function ( qa: Quaternion, qb: Quaternion, tolerance: number ): boolean {
        return _Math.equalVal( qa.w, qb.w, tolerance ) && _Math.equalVal( qa.x, qb.x, tolerance ) && _Math.equalVal( qa.y, qb.y, tolerance ) && _Math.equalVal( qa.z, qb.z, tolerance );
    },

    equalArrays: function (arr1: number[], arr2: number[]): boolean {
        if (arr1.length !== arr2.length)
            return false;

        for (var i=0; i<arr1.length; i++) {
            if (arr1[i] !== arr2[i])
                return false;
        }

        return true;
    },

    equalObjects: function (a, b): boolean {

        // Create arrays of property names
        var aProps = Object.getOwnPropertyNames(a);
        var bProps = Object.getOwnPropertyNames(b);

        // If number of properties is different,
        // objects are not equivalent
        if (aProps.length != bProps.length) {
            return false;
        }

        for (var i = 0; i < aProps.length; i++) {
            var propName = aProps[i];

            // If values of same property are not equal,
            // objects are not equivalent
            if (a[propName] !== b[propName]) {
                return false;
            }
        }

        // If we made it this far, objects
        // are considered equivalent
        return true;
    },

    equalRBTs: function (srcRBT: RBT, dstRBT: RBT): boolean {
        return (_Math.equalVec3(srcRBT.translation, dstRBT.translation, 1e-6)) && (_Math.equalQuat(srcRBT.rotation, dstRBT.rotation, 1e-6));
    },

    equalRBTsAndTileOpacities: function (srcRBT: RBT, dstRBT: RBT, srcTileOpacities, dstTileOpacities): boolean {
        return (_Math.equalVec3(srcRBT.translation, dstRBT.translation, 1e-6)) && (_Math.equalQuat(srcRBT.rotation, dstRBT.rotation, 1e-6)) && 
               ((srcTileOpacities == dstTileOpacities) || (_Math.equalObjects(srcTileOpacities, dstTileOpacities)));
    }
};


///////////////////////////////////////////////////////////////////////////////
// CameraUtils
//
// Adapted from C++ Frustum and Camera classes.
export var CameraUtils = {
    // Using the given viewport, convert view [-1,1]^2 space -> [0,W] [0,H] space.
    ScreenToView: function (InViewDims: Vector2, InOutVal: Vector2): void {
        InOutVal.x = InViewDims.x * ((InOutVal.x + 1.0) / 2.0);
        InOutVal.y = InViewDims.y * (1.0 - (InOutVal.y + 1.0) / 2.0);
    },
    GetScreen: function (camera: Camera, InWorld: Vector3): Vector2 {
        const InWorld4: Vector4 = new Vector4(InWorld.x, InWorld.y, InWorld.z, 1.0);
        const ViewProjectionMatrix: Matrix4 = camera.projectionMatrix.clone().multiply(camera.matrixWorldInverse);
        const clip: Vector4 = InWorld4.applyMatrix4(ViewProjectionMatrix);
        const InvW: number= clip.w > 0.0001 ? 1.0 / clip.w : 1.0;
        return new Vector2(clip.x * InvW, clip.y * InvW);
    },
    GetView: function (camera: Camera, InViewportDims: Vector2, InWorld: Vector3): Vector2 {
        var result : Vector2 = CameraUtils.GetScreen(camera, InWorld);
        CameraUtils.ScreenToView(InViewportDims, result);
        return result;
    },
    GetWorld: function (camera: PerspectiveCamera, u: number, v: number, InDepth: number): Vector3 {
        const InverseView : Matrix4 = camera.matrixWorld;
        const InverseProj : Matrix4 = (new Matrix4).getInverse(camera.projectionMatrix);
        u = (2.0 * u) - 1.0;
        v = 1.0 - (2.0 * v);
        v = -v;
        const depth : number = (2.0 * (InDepth - camera.near)) / (camera.far - camera.near) - 1.0;
        // I forgot to invert the perspective division in the last version.
        var beforeWorld : Vector4 = new Vector4(u, v, depth, 1).multiplyScalar(InDepth);
        beforeWorld.applyMatrix4(InverseProj);
        beforeWorld.w = 1.0;
        beforeWorld.applyMatrix4(InverseView);
        return new Vector3(beforeWorld.x, beforeWorld.y, beforeWorld.z);
    },
    GetWorld2: function (camera: PerspectiveCamera, InViewportDims: Vector2, InScreenX: number, InScreenY: number, depth: number): Vector3 {
        const x : number = InScreenX / InViewportDims.x;
        const y : number = (InViewportDims.y - InScreenY) / InViewportDims.y;
        return CameraUtils.GetWorld(camera, x, y, depth);
    },
    GetRight: function (camera: Camera): Vector3 {
        var q : Quaternion = new Quaternion;
        camera.getWorldQuaternion(q);
        return new Vector3(1,0,0).applyQuaternion(q);
    },
    GetRBT: function (camera: Camera): RBT {
        var q : Quaternion = new Quaternion;
        camera.getWorldQuaternion(q);
        return new RBT(q, camera.position);
    },
    IsVisibleSphere(frustum: Frustum, s: Sphere): boolean
    {
        // TODO: more expensive than C++ equivalent, as frustum planes aren't cached as part of the camera.
        //if ( m_bPlanesDirty )
        //    UpdateFrustumPlanes();
        const FP_NUM = 6;
        for ( var i = 0 ; i < FP_NUM; ++i )
        {
            if ( frustum.planes[i].distanceToPoint( s.center ) < -s.radius )
                return false;
        }

        return true;
    },
    IsVisibleSphere_CameraSlow(camera: Camera, s: Sphere): boolean
    {
        var frustum : Frustum = new Frustum;
        // matrixWorldInverse is the view matrix
        frustum.setFromMatrix((new Matrix4).multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse));
        return this.IsVisibleSphere(frustum, s);
    },
    IsVisiblePoint(frustum: Frustum, p: Vector3): boolean
    {
        // TODO: more expensive than C++ equivalent, as frustum planes aren't cached as part of the camera.
        //if ( m_bPlanesDirty )
        //    UpdateFrustumPlanes();

        const FP_NUM = 6;
        for ( var i = 0 ; i < FP_NUM; ++i )
        {
            if ( frustum.planes[i].distanceToPoint( p ) < 0 )
                return false;
        }

        return true;
    }
};

//////////////////////////////////////////////////////////////////////////////

// Remaps t in [0,1] according to the specified easing type.
//   In: accelerating from zero velocity.
//   Out: decelerating to zero velocity.
//   InOut: accelerating until halfway, then decelerating.
export enum EasingType
{
    ET_Linear,

    ET_QuadraticIn,
    ET_QuadraticOut,
    ET_QuadraticInOut,

    ET_CubicIn,
    ET_CubicOut,
    ET_CubicInOut,

    ET_QuarticIn,
    ET_QuarticOut,
    ET_QuarticInOut,

    ET_QuinticIn,
    ET_QuinticOut,
    ET_QuinticInOut,

    ET_SinusoidIn,
    ET_SinusoidOut,
    ET_SinusoidInOut,

    ET_ExpIn,
    ET_ExpOut,
    ET_ExpInOut
};

///////////////////////////////////////////////////////////////////////////////
// EasingFunctions
//
/*
 * Easing Functions - inspired from http://gizma.com/easing/
 * only considering the t value for the range [0, 1] => [0, 1]
 * from https://gist.github.com/gre/1650294#file-easing-js-L14
 */

export var EasingFunctions = {
    // no easing, no acceleration
    linear: function (t: number): number { return t; },
    // accelerating from zero velocity
    easeInQuad: function (t: number): number { return t * t; },
    // decelerating to zero velocity
    easeOutQuad: function (t: number): number { return t * (2 - t); },
    // acceleration until halfway, then deceleration
    easeInOutQuad: function (t: number): number { return t < .5 ? 2 * t * t : -1 + (4 - 2 * t) * t; },
    // accelerating from zero velocity
    easeInCubic: function (t: number): number { return t * t * t; },
    // decelerating to zero velocity
    easeOutCubic: function (t: number): number { return (--t) * t * t + 1; },
    // acceleration until halfway, then deceleration
    easeInOutCubic: function (t: number): number { return t < .5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1; },
    // accelerating from zero velocity
    easeInQuart: function (t: number): number { return t * t * t * t; },
    // decelerating to zero velocity
    easeOutQuart: function (t: number): number { return 1 - (--t) * t * t * t; },
    // acceleration until halfway, then deceleration
    easeInOutQuart: function (t: number): number { return t < .5 ? 8 * t * t * t * t : 1 - 8 * (--t) * t * t * t; },
    // accelerating from zero velocity
    easeInQuint: function (t: number): number { return t * t * t * t * t; },
    // decelerating to zero velocity
    easeOutQuint: function (t: number): number { return 1 + (--t) * t * t * t * t; },
    // acceleration until halfway, then deceleration
    easeInOutQuint: function (t: number): number { return t < .5 ? 16 * t * t * t * t * t : 1 + 16 * (--t) * t * t * t * t; },

    ApplyPassThroughEasing: function (t: number, prevDuration: number, curDuration: number, nextDuration: number): number
    {
        // t is normalized time: 0 <= t <= 1
        // prevDuration = previous transition duration. 0 if src bookmark not a pass-through
        // curDuration = current transition duration. Must be greater than 0
        // nextDuration = next transition duration. 0 if dst bookmark not a pass-through

        // init the 1st control point (a, b) to zero-slope value of (0.5, 0) 
        // init the 2nd control point (c, d) to zero slope value of (0.5, 1)
        var slopeNum=1, slopeDenom=1, a=0.5, b=0, c=0.5, d=1, divisor=1;
    
        if (prevDuration > 0) {
            // convert to this domain
            //prevDuration *= (prevDuration/curDuration);
            // get avg of the prev and cur transition speeds
            //slopeNum = (prevDuration + curDuration);
            //slopeDenom = (2 * prevDuration * curDuration);

            // start speed is previous transition speed
            // previous slope was 1, but we need to convert to this domain by multiplying by curDuration / prevDuration
            slopeNum = curDuration; 
            slopeDenom = prevDuration;
            // both values must be between 0 and 1
            divisor = Math.max(slopeNum, slopeDenom);
            // we will use 1/2 as one of the control points here (a or b)
            a = slopeDenom / (divisor*2);
            b = slopeNum / (divisor*2);
        }

        if (nextDuration > 0) {
            // convert to this domain
            //nextDuration *= (nextDuration/curDuration);
            // get avg of the cur and next transition speeds
            //slopeNum = (nextDuration + curDuration);
            //slopeDenom = (2 * nextDuration * curDuration);

            // end speed is cur transition speed
            slopeNum = 1;
            slopeDenom = 1;
            // both values must be between 0 and 1
            divisor = Math.max(slopeNum, slopeDenom);
            // we will use 1/2 as one of the control points here (c or d)
            c = 1 - slopeDenom / (divisor*2);
            d = 1 - slopeNum / (divisor*2);
        }
        
        return EasingFunctions.CubicBezier (t, a, b, c, d);
    },

    //--------------------------------------------------------------
    CubicBezier: function (x: number, a: number, b: number, c: number, d: number): number {

        var y0a = 0.0; // initial y
        var x0a = 0.0; // initial x 
        var y1a = b;    // 1st influence y   
        var x1a = a;    // 1st influence x 
        var y2a = d;    // 2nd influence y
        var x2a = c;    // 2nd influence x
        var y3a = 1.0; // final y 
        var x3a = 1.0; // final x 

        var A =   x3a - 3*x2a + 3*x1a - x0a;
        var B = 3*x2a - 6*x1a + 3*x0a;
        var C = 3*x1a - 3*x0a;   
        var D =   x0a;

        var E =   y3a - 3*y2a + 3*y1a - y0a;    
        var F = 3*y2a - 6*y1a + 3*y0a;             
        var G = 3*y1a - 3*y0a;             
        var H =   y0a;

        // Solve for t given x (using Newton-Raphelson), then solve for y given t.
        // Assume for the first guess that t = x.
        var currentt = x;
        var nRefinementIterations = 5;
        for (var i=0; i < nRefinementIterations; i++) {
            var currentx = EasingFunctions.XFromT (currentt, A,B,C,D); 
            var currentslope = EasingFunctions.SlopeFromT (currentt, A,B,C);
            currentt -= (currentx - x)*(currentslope);
            currentt = _Math.clamp(currentt, 0.0,1.0);
        } 

        var y = EasingFunctions.YFromT (currentt,E,F,G,H);
        return y;
    },

    // Helper functions:
    SlopeFromT: function (t: number, A: number, B: number, C: number): number {
        var dtdx = 1.0/(3.0*A*t*t + 2.0*B*t + C); 
        return dtdx;
    },

    XFromT: function (t: number, A: number, B: number, C: number, D: number): number {
        var x = A*(t*t*t) + B*(t*t) + C*t + D;
        return x;
    },

    YFromT: function (t: number, E: number, F: number, G: number, H: number): number {
        var y = E*(t*t*t) + F*(t*t) + G*t + H;
        return y;
    }
};

///////////////////////////////////////////////////////////////////////////////
// Ray
//
// Adapted from C++ Ray class, but using Javascript copy/clone/temporary conventions.
export class Ray {
    origin: Vector3;
    direction: Vector3;
    constructor(origin: Vector3, direction: Vector3) {
        this.origin = origin.clone();
        this.direction = direction.clone();
    }
    clone(): Ray {
        return new Ray(this.origin, this.direction);
    }
    GetPos(t: number): Vector3 {
        return this.direction.clone().multiplyScalar(t).add(this.origin);
    }
    ComputeTFromPos(InPos: Vector3): number {
        return this.direction.dot(InPos.clone().sub(this.origin));
    }
    // Not part of C++ class. Returns a list of 0, 1 or 2 items.
    // - If ReturnT=true,  returns the ray parameter t for the solution(s)
    // - If ReturnT=false, returns the 3D points for the solution(s)
    IntersectWithSphere(sphere: Sphere, returnT: boolean = false): number | Vector3[] {
        var dir = this.direction.clone().normalize();
        var oc = this.origin.clone().sub(sphere.center);
        var lDotOC = dir.dot(oc);
        var term = _Math.sqr(lDotOC) - oc.lengthSq() + _Math.sqr(sphere.radius);
        var result = [];
        if (term == 0) {
            result = [-lDotOC];
            if (!returnT)
                result = [ this.origin.clone().add(dir.clone().multiplyScalar(result[0])) ];
        }
        else if (term > 0) {
            term = Math.sqrt(term);
            result = [-lDotOC + term, -lDotOC - term];
            if (!returnT)
                result = [
                    this.origin.clone().add(dir.clone().multiplyScalar(result[0])),
                    this.origin.clone().add(dir.clone().multiplyScalar(result[1])) ];
        }
        return result;
    }
};

///////////////////////////////////////////////////////////////////////////////
// RBT
//
// Rigid Body Transform
// Adapted from C++ RBT class, but using Javascript copy/clone/temporary conventions.
export class RBT {
    rotation: Quaternion;
    translation: Vector3;
    constructor(rotation: Quaternion, translation: Vector3) {
        this.rotation = rotation.clone();
        this.translation = translation.clone();
    }
    clone() {
        return new RBT(this.rotation, this.translation);
    }
    set(rotation: Quaternion, translation: Vector3): void {
        this.rotation.copy(rotation);
        this.translation.copy(translation);
    }
    copy(rhs: RBT): RBT {
        this.set(rhs.rotation, rhs.translation);
        return this;
    }
    inverse(): RBT {
        this.rotation.inverse(); // Auto-normalizes
        this.translation.applyQuaternion(this.rotation).negate();
        return this;
    }
    multiply(rhs: RBT): RBT {
        this.translation.add(rhs.translation.clone().applyQuaternion(this.rotation));
        this.rotation.multiply(rhs.rotation).normalize();
        return this;
    }
    static multiplyRBTs(lhs: RBT, rhs: RBT): RBT {
        return lhs.clone().multiply(rhs);
    }
	// Implies w = 1.0 - use an explicit Vec4(x,y,z,0) if you want to transform a vector and not a point.
    multiplyVec3(v: Vector3): Vector3 {
        return v.clone().applyQuaternion(this.rotation).add(this.translation);
    }
    multiplyVec4(v: Vector4): Vector4 {
        // Implementation of Quat * Vector4 operator.
        const v3 : Vector3 = new Vector3(v.x, v.y, v.z);
        const v3Trans : Vector3 = v3.clone().applyQuaternion(this.rotation);
        const v4Trans : Vector4 = new Vector4(v3Trans.x, v3Trans.y, v3Trans.z, v.w);

        const t3: Vector3 = this.translation.clone().multiplyScalar(v.w);
        return new Vector4(t3.x, t3.y, t3.z, v.w).add(v4Trans);
	}
    doToWRT(frame: RBT, aframe: RBT): RBT {  // const - does not affect this.
        var result = aframe.clone().multiply(this);
        result.multiply(aframe.clone().inverse().multiply(frame));
        return result;
    }
	getZAxis3(): Vector3
	{
		const zaxis4: Vector4 = this.getZAxis4();
		return new Vector3(zaxis4.x, zaxis4.y, zaxis4.z);
	}
    getZAxis4(): Vector4
    {
        return this.multiplyVec4(new Vector4(0,0,1,0));
    }
    toString(precision: number = 3): string
    {
        return this.translation.toArray()
            .concat(this.rotation.toArray())
            .map(value => value.toFixed(precision))
            .join(',');
    }
};

///////////////////////////////////////////////////////////////////////////////
// THREE.Sphere additional methods
//
// Merge rhs into this sphere.
export var MergeSpheres = function (lhs: Sphere, rhs: Sphere): Sphere {
    var Delta = rhs.center.clone().sub(lhs.center);
    var SqrDist = Delta.lengthSq();
    var RadDiff = rhs.radius - lhs.radius;
    if (RadDiff * RadDiff >= SqrDist) {
        if (RadDiff > 0.0)
            lhs.copy(rhs);
        return lhs;
    }
    var Dist = Math.sqrt(SqrDist);
    var EPS = 0.0000001;
    if (Dist > EPS)
        lhs.center.add(Delta.multiplyScalar((Dist + RadDiff) / (2.0 * Dist)));
    lhs.radius = 0.5 * (Dist + lhs.radius + rhs.radius);
    return lhs;
};

///////////////////////////////////////////////////////////////////////////////
// SphericalWithRoll
//
// Adapted from C++ SphericalAngle class, implemented as a subclass of THREE's Spherical class.
// Differences:
// - C++ uses "elevation" (-pi/2 to +pi/2) while THREE uses "phi" (0 to +pi)
// - C++ includes a roll parameter, THREE includes a radius parameter
export class SphericalWithRoll extends Spherical {
    roll: number;
    constructor(radius: number = 0, phi: number = 0, theta: number = 0, roll: number = 0) {
        super(radius, phi, theta);
        this.roll = roll;
    }
    clone(): this  {
        var result = Spherical.prototype.clone.call(this);
        result.roll = this.roll;
        return result;
    }
    getElevation(): number {
        return Math.PI / 2 - this.phi;
    }
    setElevation(elev: number): SphericalWithRoll {
        this.phi = Math.PI / 2 - elev;
        return this;
    }
    setFromQuat(InQuat: Quaternion): SphericalWithRoll {
        var q = InQuat.clone().normalize();
        var m4 = new Matrix4;
        m4.makeRotationFromQuaternion(q);
        var xaxis = new Vector3, yaxis = new Vector3, zaxis = new Vector3;
        m4.extractBasis(xaxis, yaxis, zaxis);
        this.setFromVector3(zaxis);
        var q_azim = new Quaternion, q_elev = new Quaternion;
        q_azim.setFromAxisAngle(new Vector3(0, 1, 0), this.theta);
        q_elev.setFromAxisAngle(new Vector3(-1, 0, 0), this.getElevation());
        var xaxis_wo_roll = new Vector3(1, 0, 0);
        q_azim.multiply(q_elev);
        xaxis_wo_roll.applyQuaternion(q_azim);
        this.roll = Math.atan2(xaxis.clone().cross(xaxis_wo_roll).length(), xaxis.dot(xaxis_wo_roll));
        return this;
    }
    toQuat(): Quaternion {
        var q_azim = (new Quaternion).setFromAxisAngle(new Vector3(0, 1, 0), this.theta);
        var q_elev = (new Quaternion).setFromAxisAngle(new Vector3(-1, 0, 0), this.getElevation());
        var q_roll = (new Quaternion).setFromAxisAngle(new Vector3(0, 0, -1), this.roll);

        return q_azim.multiply(q_elev).multiply(q_roll);
    }
    isEqual(rhs: SphericalWithRoll, EPS: number): boolean {
        EPS = EPS || 0.001 * _Math.DEG2RAD;
        return Math.abs(this.theta - rhs.theta) < EPS &&
            Math.abs(this.phi - rhs.phi) < EPS &&
            Math.abs(this.roll - rhs.roll) < EPS &&
            Math.abs(this.radius - rhs.radius) < EPS;
    }
};
